import React, { useEffect, useMemo, useState } from "react";
import { EnvelopeIcon, XMarkIcon, DocumentArrowDownIcon, EyeIcon } from "@heroicons/react/24/outline";
import { Button } from "../../ui";
import VaptStatusBadge from "./VaptStatusBadge";
import { DataTable } from "../../common/DataTable";
import {
  listVaptProjectsApi,
  getVaptExecutionCoverageApi,
  listVaptExceptionsApi,
  listVaptNotificationHistoryApi,
  sendVaptCoverageNotificationApi,
  getAutomaticNotificationConfigApi,
  updateAutomaticNotificationConfigApi,
  generateVaptReportApi,
  downloadVaptReportApi,
} from "../../../services/axios/AuthApis/VAPTApis";

const COVERAGE_STATUSES = ["Implemented", "Not Implemented", "Unsupported", "Not Applicable", "Out of Scope", "Blocked"];

const STATUS_DEFINITIONS = {
  Implemented: "The target was applicable and at least one tool completed execution against it.",
  "Not Implemented": "The target is applicable but no tool has completed execution against it yet.",
  Unsupported: "The target is recognized, but the required VAPT capability/tool is not currently available.",
  "Not Applicable": "The target exists but there is no applicable VAPT methodology for the selected assessment.",
  "Out of Scope": "The target exists but was intentionally excluded from the approved engagement scope.",
  Blocked: "The target could not be tested because authorization or another required validation was missing.",
};

const triggerDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Detail drawer for a single exception row — read-only, matches the
// existing VaptNotificationPreviewModal overlay pattern.
const ExceptionDetailModal = ({ row, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
    <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-gray-900">Exception Details</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>
      <div className="space-y-3 text-sm">
        <div>
          <p className="text-[10px] text-gray-400 uppercase">Target</p>
          <p className="text-gray-800 font-medium">{row.target}</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-[10px] text-gray-400 uppercase">Target Type</p>
            <p className="text-gray-800">{row.target_type || "—"}</p>
          </div>
          <div>
            <p className="text-[10px] text-gray-400 uppercase">Domain</p>
            <p className="text-gray-800">{row.domain || "—"}</p>
          </div>
          <div>
            <p className="text-[10px] text-gray-400 uppercase">Coverage Status</p>
            <VaptStatusBadge status={row.status} size="sm" />
          </div>
        </div>
      </div>
      <div className="flex justify-end mt-6">
        <Button variant="secondary" onClick={onClose}>Close</Button>
      </div>
    </div>
  </div>
);

// Email-status → colour / message for the post-send screen. The backend is
// the single source of truth for delivery status; the UI never claims a
// notification was sent on its own.
const EMAIL_STATUS_STYLE = {
  Sent: { cls: "text-green-700", label: "✓ Notification sent to the customer contact.", short: "Sent" },
  "Test Mode (not actually delivered)": {
    cls: "text-amber-600",
    label: "Notification recorded. Email delivery is running in test mode.",
    short: "Test mode",
  },
  "Email provider not configured": {
    cls: "text-amber-600",
    label: "Notification recorded. Email delivery is not configured for this environment.",
    short: "Not configured",
  },
  Failed: {
    cls: "text-red-600",
    label: "Notification recorded, but email delivery failed.",
    short: "Failed",
  },
};

// Manual "Send Customer Notification" modal. Recipient is resolved
// server-side from the project's registered customer contact.
const SendNotificationModal = ({ assessment, sending, sent, result, onCancel, onSend }) => {
  const style = result ? EMAIL_STATUS_STYLE[result.email_status] || {
    cls: "text-gray-700",
    label: "Notification recorded.",
    short: "Recorded",
  } : null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={sent ? undefined : onCancel}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        {!sent ? (
          <>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-gray-900">Send Customer Notification</h3>
              <button onClick={onCancel} className="text-gray-400 hover:text-gray-700">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-2 text-sm mb-4">
              <p><span className="text-gray-500">Customer:</span> <span className="font-medium text-gray-800">{assessment?.customer || "—"}</span></p>
              <p><span className="text-gray-500">Recipient:</span> <span className="font-medium text-gray-800">the project&apos;s registered customer contact</span></p>
              <p><span className="text-gray-500">Subject:</span> <span className="font-medium text-gray-800">VAPT Assessment – Coverage &amp; Exceptions Summary</span></p>
            </div>
            <div className="border border-gray-100 rounded-lg p-3 mb-4">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">The notification includes</p>
              <ul className="space-y-1 text-xs text-gray-700">
                {[
                  "Risk score & severity breakdown",
                  "Open vs total findings",
                  "Coverage: implemented / not implemented / unsupported / out of scope / blocked",
                  "Open scope exceptions",
                  "VAPT report reference (if a report id is supplied)",
                ].map((item) => (
                  <li key={item}>✓ {item}</li>
                ))}
              </ul>
            </div>
            <p className="text-[11px] text-gray-500 mb-4">
              A notification record is always created. Email delivery depends on the configured mail provider.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={onCancel}>Cancel</Button>
              <Button loading={sending} onClick={onSend}>Send Notification</Button>
            </div>
          </>
        ) : (
          <div className="text-center py-4">
            <p className={`font-semibold text-sm mb-3 ${style?.cls || "text-gray-700"}`}>{style?.label}</p>
            <div className="text-sm text-gray-700 space-y-1 mb-5 text-left">
              <p><span className="text-gray-500">Recipient:</span> {result?.recipient || "—"}</p>
              <p><span className="text-gray-500">Email status:</span> {style?.short || "—"}</p>
              {result?.email_error && (
                <p className="text-red-600 text-xs"><span className="text-gray-500">Error:</span> {result.email_error}</p>
              )}
              <p><span className="text-gray-500">Recorded:</span> {result?.sent_at ? new Date(result.sent_at).toLocaleString() : new Date().toLocaleString()}</p>
            </div>
            <Button onClick={onCancel}>Close</Button>
          </div>
        )}
      </div>
    </div>
  );
};

const VaptCoverageExceptionsPanel = () => {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [coverage, setCoverage] = useState(null);
  const [rows, setRows] = useState([]);
  const [history, setHistory] = useState([]);
  const [autoConfig, setAutoConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingCoverage, setLoadingCoverage] = useState(false);

  const [detailRow, setDetailRow] = useState(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generatedReport, setGeneratedReport] = useState(null);
  const [downloading, setDownloading] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    Promise.all([listVaptProjectsApi(), listVaptNotificationHistoryApi(), getAutomaticNotificationConfigApi()])
      .then(([projs, h, cfg]) => {
        const list = Array.isArray(projs) ? projs : projs?.results || [];
        setProjects(list);
        if (list[0]) setProjectId(list[0].id || list[0].project_id);
        setHistory(h);
        setAutoConfig(cfg);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const project = useMemo(() => projects.find((p) => (p.id || p.project_id) === projectId), [projects, projectId]);

  const loadCoverage = () => {
    if (!project) return;
    setLoadingCoverage(true);
    setGeneratedReport(null);
    Promise.all([
      getVaptExecutionCoverageApi(project.project_id),
      listVaptExceptionsApi({ project_id: project.project_id }),
    ])
      .then(([cov, exc]) => {
        setCoverage(cov);
        setRows(
          (cov?.detail?.rows || []).map((r, i) => ({
            id: i,
            target: r.target,
            target_type: r.target_type,
            domain: r.domain,
            status: r.coverage_status,
          }))
        );
        setExceptions(Array.isArray(exc) ? exc : exc?.results || []);
      })
      .finally(() => setLoadingCoverage(false));
  };

  const [exceptions, setExceptions] = useState([]);

  useEffect(() => {
    if (project) loadCoverage();
  }, [project]);

  const summary = useMemo(() => {
    const s = coverage?.detail?.summary || coverage?.summary || {};
    return {
      total: s.total || 0,
      implemented: s.implemented || 0,
      notImplemented: s.not_implemented || 0,
      unsupported: s.unsupported || 0,
      notApplicable: s.not_applicable || 0,
      outOfScope: s.out_of_scope || 0,
      blocked: s.blocked || 0,
    };
  }, [coverage]);

  const targetTypes = useMemo(() => Array.from(new Set(rows.map((r) => r.target_type).filter(Boolean))), [rows]);

  const openSendModal = () => {
    setSent(false);
    setSendResult(null);
    setShowSendModal(true);
  };

  const handleSend = async () => {
    if (!project) return;
    setSending(true);
    try {
      const res = await sendVaptCoverageNotificationApi({
        projectId: project.project_id,
        reportId: generatedReport?.report_id,
      });
      setSendResult(res);
      setHistory(await listVaptNotificationHistoryApi(project.project_id));
      setSent(true);
    } catch (e) {
      setSendResult({
        email_status: "Failed",
        email_error: e?.response?.data?.error || e?.message || "Request failed",
        recipient: "—",
      });
      setSent(true);
    } finally {
      setSending(false);
    }
  };

  const handleToggleAuto = async () => {
    const updated = await updateAutomaticNotificationConfigApi({ enabled: !autoConfig.enabled });
    setAutoConfig(updated);
  };

  const handleTriggerChange = async (trigger) => {
    const updated = await updateAutomaticNotificationConfigApi({ trigger });
    setAutoConfig(updated);
  };

  const handleGenerateReport = async () => {
    if (!project) return;
    setGenerating(true);
    setGeneratedReport(null);
    setNotice("Generating Coverage Report…");
    try {
      const created = await generateVaptReportApi({ project: project.id, report_type: "Coverage", file_format: "HTML" });
      if (created?.status === "Generated") {
        setGeneratedReport(created);
        setNotice("✓ Coverage report generated successfully.");
      } else if (created?.status === "Failed") {
        setNotice(`Coverage report generation failed: ${created.file_error || "unknown error"}`);
      } else {
        setNotice("Coverage report record created but file generation is still pending.");
      }
    } catch (err) {
      setNotice(err?.response?.data?.error || err?.response?.data?.detail || err?.message || "Failed to generate coverage report.");
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (format) => {
    if (!generatedReport) return;
    setDownloading(format);
    try {
      let reportId = generatedReport.report_id;
      if (generatedReport.file_format !== format) {
        const created = await generateVaptReportApi({ project: project.id, report_type: "Coverage", file_format: format });
        reportId = created.report_id;
        setGeneratedReport(created);
      }
      const blob = await downloadVaptReportApi(reportId);
      triggerDownload(blob, `${reportId}.${format.toLowerCase()}`);
    } catch (err) {
      setNotice(err?.response?.data?.error || err?.message || `Failed to download ${format} report.`);
    } finally {
      setDownloading("");
    }
  };

  const handlePreview = () => {
    if (!generatedReport) return;
    downloadVaptReportApi(generatedReport.report_id)
      .then((blob) => {
        const url = window.URL.createObjectURL(new Blob([blob], { type: "text/html" }));
        window.open(url, "_blank");
      })
      .catch(() => setNotice("Failed to load report preview."));
  };

  if (loading) return <p className="text-sm text-gray-400">Loading coverage &amp; exceptions…</p>;

  if (projects.length === 0) {
    return (
      <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
        <p className="text-gray-500">No VAPT projects available to show coverage for.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-base font-bold text-gray-900">Coverage &amp; Exceptions</h3>
          <p className="text-sm text-gray-500 max-w-2xl">
            Assets and targets that were excluded, unsupported, blocked, or not applicable during the VAPT assessment.
          </p>
        </div>
        <Button variant="secondary" icon={EnvelopeIcon} onClick={openSendModal}>
          Send Customer Notification
        </Button>
      </div>

      {/* Assessment info */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div>
          <p className="text-[10px] text-gray-400 uppercase">Project</p>
          <select
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            className="border border-gray-200 rounded-lg px-2 py-1 text-sm font-medium text-gray-800 mt-1 w-full"
          >
            {projects.map((p) => (
              <option key={p.id || p.project_id} value={p.id || p.project_id}>{p.name} ({p.project_id})</option>
            ))}
          </select>
        </div>
        <div>
          <p className="text-[10px] text-gray-400 uppercase">Assessment Type</p>
          <p className="text-gray-800 font-medium">{project?.engagement_type === "asset" ? "Asset VAPT" : "External VAPT"}</p>
        </div>
        <div>
          <p className="text-[10px] text-gray-400 uppercase">Scan Status</p>
          <VaptStatusBadge status={project?.status} />
        </div>
        <div>
          <p className="text-[10px] text-gray-400 uppercase">Generated Date</p>
          <p className="text-gray-800 font-medium">{generatedReport ? new Date().toLocaleString() : "Not yet generated"}</p>
        </div>
      </div>

      {notice && (
        <div className="bg-amber-50 border border-amber-200 text-amber-700 text-sm rounded-lg px-4 py-2.5">{notice}</div>
      )}

      {loadingCoverage ? (
        <p className="text-sm text-gray-400">Loading coverage data…</p>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {[
              ["Total Targets", summary.total, "text-gray-800"],
              ["Implemented", summary.implemented, "text-green-700"],
              ["Not Implemented", summary.notImplemented, "text-amber-600"],
              ["Unsupported", summary.unsupported, "text-amber-600"],
              ["Not Applicable", summary.notApplicable, "text-gray-500"],
              ["Out of Scope", summary.outOfScope, "text-gray-500"],
              ["Blocked", summary.blocked, "text-red-700"],
            ].map(([label, value, color]) => (
              <div key={label} className="bg-white border border-gray-200 rounded-xl p-4">
                <p className={`text-2xl font-bold ${color}`}>{value}</p>
                <p className="text-xs text-gray-500 mt-1">{label}</p>
              </div>
            ))}
          </div>

          {/* Tool coverage by domain */}
          {coverage?.detail?.tool_coverage && Object.keys(coverage.detail.tool_coverage).length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Tool Coverage</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                {Object.entries(coverage.detail.tool_coverage).map(([domain, tools]) => (
                  <p key={domain}><span className="font-medium text-gray-800">{domain}:</span> {tools.join(", ")}</p>
                ))}
              </div>
            </div>
          )}

          {/* Status definitions */}
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Status Definitions</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-xs">
              {COVERAGE_STATUSES.map((s) => (
                <div key={s} className="flex gap-2">
                  <span className="font-semibold flex-shrink-0"><VaptStatusBadge status={s} size="sm" /></span>
                  <span className="text-gray-600">{STATUS_DEFINITIONS[s]}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Report actions */}
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="secondary" icon={DocumentArrowDownIcon} loading={generating} onClick={handleGenerateReport}>
              Generate Coverage Report
            </Button>
            {generatedReport && (
              <>
                <Button variant="secondary" icon={EyeIcon} onClick={handlePreview}>
                  Preview Report
                </Button>
                <Button variant="secondary" loading={downloading === "PDF"} onClick={() => handleDownload("PDF")}>
                  Download PDF
                </Button>
                <Button variant="secondary" loading={downloading === "HTML"} onClick={() => handleDownload("HTML")}>
                  Download HTML
                </Button>
                <Button variant="secondary" loading={downloading === "CSV"} onClick={() => handleDownload("CSV")}>
                  Download CSV
                </Button>
              </>
            )}
          </div>

          {/* Target-wise coverage table */}
          <DataTable
            columns={[
              {
                id: "target",
                accessorFn: (r) => r.target,
                header: "Target",
                enableSorting: false,
                meta: { filter: { variant: "text", label: "Target", placeholder: "Search target" } },
                cell: ({ row }) => <span>{row.original.target}</span>,
              },
              {
                id: "target_type",
                accessorFn: (r) => r.target_type,
                header: "Target Type",
                enableSorting: false,
                meta: {
                  filter: {
                    variant: "select",
                    label: "Target Type",
                    placeholder: "All target types",
                    options: targetTypes.map((v) => ({ value: v, label: v })),
                  },
                },
                cell: ({ row }) => <span>{row.original.target_type}</span>,
              },
              {
                id: "domain",
                accessorFn: (r) => r.domain,
                header: "Domain",
                enableSorting: false,
                meta: { filter: { variant: "text", label: "Domain", placeholder: "Search domain" } },
                cell: ({ row }) => <span>{row.original.domain}</span>,
              },
              {
                id: "status",
                accessorFn: (r) => r.status,
                header: "Coverage Status",
                enableSorting: false,
                meta: {
                  filter: {
                    variant: "select",
                    label: "Coverage Status",
                    placeholder: "All statuses",
                    options: COVERAGE_STATUSES.map((s) => ({ value: s, label: s })),
                  },
                },
                cell: ({ row }) => <VaptStatusBadge status={row.original.status} size="sm" />,
              },
              {
                id: "action",
                header: "Action",
                enableSorting: false,
                cell: ({ row }) => (
                  <button
                    type="button"
                    onClick={() => setDetailRow(row.original)}
                    className="text-xs font-medium text-primary-600 hover:text-primary-700"
                  >
                    View
                  </button>
                ),
              },
            ]}
            data={rows}
            getRowId={(r) => String(r.id)}
            emptyMessage="No targets match this filter."
            enableColumnVisibility={false}
            enableColumnResizing
            enableColumnFilters
            maxBodyHeight="65vh"
          />

          {/* Real scope exceptions */}
          <div>
            <p className="text-sm font-semibold text-gray-800 mb-3">Scope Exceptions</p>
            <DataTable
              columns={[
                {
                  id: "target_name",
                  accessorFn: (r) => r.target_name || r.target || "—",
                  header: "Target",
                  enableSorting: false,
                  meta: { filter: { variant: "text", label: "Target", placeholder: "Search target" } },
                  cell: ({ row }) => <span>{row.original.target_name || row.original.target || "—"}</span>,
                },
                {
                  id: "reason",
                  accessorFn: (r) => r.reason,
                  header: "Reason",
                  enableSorting: false,
                  meta: { filter: { variant: "text", label: "Reason", placeholder: "Search reason" } },
                  cell: ({ row }) => <span>{row.original.reason}</span>,
                },
                {
                  id: "requested_by",
                  accessorFn: (r) => r.requested_by || "—",
                  header: "Requested By",
                  enableSorting: false,
                  meta: { filter: { variant: "text", label: "Requested By", placeholder: "Search requester" } },
                  cell: ({ row }) => <span>{row.original.requested_by || "—"}</span>,
                },
                {
                  id: "approved_by",
                  accessorFn: (r) => r.approved_by || "—",
                  header: "Approved/Rejected By",
                  enableSorting: false,
                  cell: ({ row }) => <span>{row.original.approved_by || "—"}</span>,
                },
                {
                  id: "status",
                  accessorFn: (r) => r.status,
                  header: "Status",
                  enableSorting: false,
                  meta: {
                    filter: {
                      variant: "select",
                      label: "Status",
                      placeholder: "All statuses",
                      options: [...new Set(exceptions.map((e) => e.status).filter(Boolean))].map((v) => ({ value: v, label: v })),
                    },
                  },
                  cell: ({ row }) => <VaptStatusBadge status={row.original.status} size="sm" />,
                },
              ]}
              data={exceptions.map((e) => ({ ...e, id: e.exception_id }))}
              getRowId={(r) => String(r.id)}
              emptyMessage="No scope exceptions recorded for this project."
              enableColumnVisibility={false}
              enableColumnResizing
              enableColumnFilters
              maxBodyHeight="65vh"
            />
          </div>
        </>
      )}

      {/* Automatic notification config */}
      {autoConfig && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-800">Customer Notification — Automatic Notification</p>
            <label className="inline-flex relative items-center cursor-pointer">
              <input type="checkbox" checked={autoConfig.enabled} onChange={handleToggleAuto} className="sr-only peer" />
              <div
                className="w-9 h-5 rounded-full peer peer-checked:bg-green-600 transition-all duration-300"
                style={{ backgroundColor: autoConfig.enabled ? "" : "#9ca3af" }}
              >
                <div
                  className="absolute top-[2px] left-[2px] h-4 w-4 bg-white rounded-full transition-all duration-300 shadow-sm"
                  style={{ transform: autoConfig.enabled ? "translateX(16px)" : "translateX(0)" }}
                />
              </div>
            </label>
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Trigger</p>
            <div className="space-y-1.5">
              {[
                ["completed", "Scan Completed"],
                ["exceptions", "Scan Completed with Exceptions"],
              ].map(([value, label]) => (
                <label key={value} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="radio"
                    name="auto-trigger"
                    checked={autoConfig.trigger === value}
                    onChange={() => handleTriggerChange(value)}
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Recipients</p>
            <p className="text-sm text-gray-700">{(autoConfig.recipients || []).join(", ")}</p>
          </div>

          <p className="text-xs text-gray-400">
            Automatic notification sends the customer a consolidated coverage and exception summary after the VAPT
            assessment is completed.
          </p>
        </div>
      )}

      {/* Notification history */}
      <div>
        <p className="text-sm font-semibold text-gray-800 mb-3">Notification History</p>
        <DataTable
          columns={[
            {
              id: "date",
              accessorFn: (r) => r.date,
              header: "Date",
              enableSorting: false,
              cell: ({ row }) => <span>{new Date(row.original.date).toLocaleString()}</span>,
            },
            {
              id: "type",
              accessorFn: (r) => r.type,
              header: "Type",
              enableSorting: false,
              meta: {
                filter: {
                  variant: "select",
                  label: "Type",
                  placeholder: "All types",
                  options: [...new Set(history.map((h) => h.type).filter(Boolean))].map((v) => ({ value: v, label: v })),
                },
              },
              cell: ({ row }) => <span>{row.original.type}</span>,
            },
            {
              id: "recipient",
              accessorFn: (r) => r.recipient,
              header: "Recipient",
              enableSorting: false,
              meta: { filter: { variant: "text", label: "Recipient", placeholder: "Search recipient" } },
              cell: ({ row }) => <span>{row.original.recipient}</span>,
            },
            {
              id: "status",
              accessorFn: (r) => r.status,
              header: "Status",
              enableSorting: false,
              meta: {
                filter: {
                  variant: "select",
                  label: "Status",
                  placeholder: "All statuses",
                  options: [...new Set(history.map((h) => h.status).filter(Boolean))].map((v) => ({ value: v, label: v })),
                },
              },
              cell: ({ row }) => <VaptStatusBadge status={row.original.status} size="sm" />,
            },
          ]}
          data={history}
          getRowId={(r, i) => String(r.id ?? i)}
          emptyMessage="No notifications have been sent."
          enableColumnVisibility={false}
          enableColumnResizing
          enableColumnFilters
          maxBodyHeight="65vh"
        />
      </div>

      {detailRow && <ExceptionDetailModal row={detailRow} onClose={() => setDetailRow(null)} />}

      {showSendModal && (
        <SendNotificationModal
          assessment={project ? { customer: project.client_name || project.name } : null}
          sending={sending}
          sent={sent}
          result={sendResult}
          onCancel={() => setShowSendModal(false)}
          onSend={handleSend}
        />
      )}
    </div>
  );
};

export default VaptCoverageExceptionsPanel;
